import 'swiper/css';
import 'swiper/css/pagination';
import '../scss/main.scss';

import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';

import { menuItems } from './data';
import { mediaController } from './media-controller';
import { initSearchOverlay } from './search-overlay';
import { StoryViewer } from './story-viewer';

const currentPage = document.body.dataset.page || 'home';

const parseStoryItem = (node) => ({
  eyebrow: node.dataset.storyEyebrow || '',
  title: node.dataset.storyTitle || '',
  image: node.dataset.storyImage || '',
  alt: node.dataset.storyAlt || '',
  duration: Number(node.dataset.storyDuration || 5000)
});

const parseStoryGroup = (id) => {
  const group = document.getElementById(id);

  if (!group) {
    return null;
  }

  return Array.from(group.querySelectorAll('[data-story-item]')).map(parseStoryItem);
};

const parseHighlightGroup = (id) => {
  const group = document.getElementById(id);

  if (!group) {
    return null;
  }

  return {
    title: group.dataset.highlightTitle || '',
    image: group.dataset.highlightImage || '',
    stories: Array.from(group.querySelectorAll('[data-story-item]')).map(parseStoryItem)
  };
};

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const renderMenu = (container, items, modifier = '') => {
  if (!container) return;

  container.innerHTML = items
    .map((item) => {
      const isShopContext = ['product', 'cart', 'checkout', 'account', 'order-received'].includes(currentPage);
      const isTestsContext = ['test-single'].includes(currentPage);
      const isActive = isShopContext ? item.key === 'shop' : isTestsContext ? item.key === 'tests' : item.key === currentPage;

      return `
        <a class="site-nav__link ${modifier} ${isActive ? 'is-active' : ''}" href="${escapeHtml(item.href)}" ${
          isActive ? 'aria-current="page"' : ''
        }>
          ${escapeHtml(item.label)}
        </a>
      `;
    })
    .join('');
};

const initPostSliders = () => {
  document.querySelectorAll('.js-post-slider').forEach((slider) => {
    const pagination = slider.querySelector('.swiper-pagination');
    new Swiper(slider, {
      modules: [Pagination],
      loop: true,
      pagination: {
        el: pagination,
        clickable: true
      }
    });
  });
};

const initMediaCards = () => {
  document.querySelectorAll('.js-media').forEach((mediaElement) => {
    mediaController.register(mediaElement);
  });

  document.querySelectorAll('[data-media-trigger]').forEach((button) => {
    button.addEventListener('click', () => {
      const mediaArea = button.closest('.post-media');
      const mediaElement = mediaArea?.querySelector('.js-media');

      if (!mediaElement) return;

      mediaArea.classList.remove('has-media-error');

      if (!mediaElement.paused) {
        mediaElement.pause();
        if (mediaElement.tagName === 'AUDIO') {
          button.setAttribute('aria-label', 'پخش پادکست');
        }
        return;
      }

      mediaController.pauseAll(mediaElement);
      mediaElement.hidden = false;

      const playPromise = mediaElement.play();
      if (playPromise) {
        playPromise.catch(() => {
          mediaArea.classList.add('has-media-error');
        });
      }

      if (mediaElement.tagName === 'AUDIO') {
        button.setAttribute('aria-label', 'توقف پادکست');
      }
    });
  });
};

const initStories = () => {
  const storyRoot = document.querySelector('[data-story-viewer]');
  if (!storyRoot) return;

  const storyViewer = new StoryViewer(storyRoot, { mediaController });

  document.querySelector('[data-profile-story]')?.addEventListener('click', (event) => {
    const sourceId = event.currentTarget?.dataset.storySource;
    const stories = parseStoryGroup(sourceId) || [];
    storyViewer.openProfile(stories);
  });

  document.querySelectorAll('[data-highlight-index]').forEach((button) => {
    button.addEventListener('click', () => {
      const cards = Array.from(document.querySelectorAll('[data-highlight-index]'));
      const highlights = cards
        .map((card) => {
          const sourceId = card.dataset.storySource;
          return parseHighlightGroup(sourceId);
        })
        .filter(Boolean);

      storyViewer.openHighlight(highlights, Number(button.dataset.highlightIndex));
    });
  });
};

const initInfiniteLoadingMock = () => {
  const indicator = document.querySelector('[data-load-more]');
  if (!indicator || !('IntersectionObserver' in window)) return;

  let isTouched = false;
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting && !isTouched) {
        isTouched = true;
        indicator.classList.add('is-loading');

        window.setTimeout(() => {
          indicator.classList.remove('is-loading');
          isTouched = false;
        }, 1100);
      }
    },
    { rootMargin: '120px' }
  );

  observer.observe(indicator);
};

const initHomePage = () => {
  initPostSliders();
  initMediaCards();
  initStories();
  initInfiniteLoadingMock();
};

const initAboutPage = () => {};
const initShopPage = () => {};
const initProductPage = () => {
  document.querySelectorAll('.product-preview-strip button').forEach((button) => {
    button.addEventListener('click', () => {
      const preview = button.closest('.product-preview-strip');
      preview?.querySelectorAll('button').forEach((item) => item.classList.remove('is-active'));
      button.classList.add('is-active');
    });
  });

  document.querySelectorAll('[data-quantity]').forEach((quantity) => {
    const input = quantity.querySelector('[data-quantity-input]');
    if (!input) return;

    quantity.querySelector('[data-quantity-increase]')?.addEventListener('click', () => {
      input.value = String(Math.max(1, Number(input.value || 1) + 1));
    });

    quantity.querySelector('[data-quantity-decrease]')?.addEventListener('click', () => {
      input.value = String(Math.max(1, Number(input.value || 1) - 1));
    });
  });
};
const initCartPage = () => {};
const initCheckoutPage = () => {};
const initAccountPage = () => {
  document.querySelectorAll('[data-copy-code]').forEach((button) => {
    button.addEventListener('click', async () => {
      const code = button.dataset.copyCode;
      if (!code) return;

      try {
        await navigator.clipboard.writeText(code);
        const originalLabel = button.textContent;
        button.textContent = 'کپی شد';
        window.setTimeout(() => {
          button.textContent = originalLabel;
        }, 1600);
      } catch {
        button.textContent = 'کپی نشد';
      }
    });
  });
};
const initOrderReceivedPage = () => {};
const initAuthPage = () => {};
const initVerifyCodePage = () => {
  const inputs = Array.from(document.querySelectorAll('.verify-code-grid__input'));

  if (!inputs.length) return;

  const focusInput = (index) => {
    const target = inputs[index];
    if (!target) return;
    target.focus();
    target.select();
  };

  focusInput(0);

  inputs.forEach((input, index) => {
    input.addEventListener('input', (event) => {
      const digitsOnly = event.currentTarget.value.replace(/\D/g, '');
      event.currentTarget.value = digitsOnly.slice(0, 1);

      if (event.currentTarget.value && index < inputs.length - 1) {
        focusInput(index + 1);
      }
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Backspace' && !event.currentTarget.value && index > 0) {
        focusInput(index - 1);
      }

      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault();
        focusInput(index - 1);
      }

      if (event.key === 'ArrowRight' && index < inputs.length - 1) {
        event.preventDefault();
        focusInput(index + 1);
      }
    });

    input.addEventListener('focus', () => {
      input.select();
    });

    input.addEventListener('paste', (event) => {
      event.preventDefault();
      const pastedDigits = (event.clipboardData?.getData('text') || '').replace(/\D/g, '');

      if (!pastedDigits) return;

      inputs.forEach((field, fieldIndex) => {
        field.value = pastedDigits[fieldIndex] || '';
      });

      focusInput(Math.min(pastedDigits.length, inputs.length) - 1);
    });
  });
};

const init = () => {
  renderMenu(document.querySelector('[data-desktop-menu]'), menuItems);
  renderMenu(document.querySelector('[data-mobile-menu]'), menuItems, 'site-nav__link--mobile');
  initSearchOverlay();

  switch (currentPage) {
    case 'about':
      initAboutPage();
      break;
    case 'shop':
      initShopPage();
      break;
    case 'product':
      initProductPage();
      break;
    case 'cart':
      initCartPage();
      break;
    case 'checkout':
      initCheckoutPage();
      break;
    case 'account':
      initAccountPage();
      break;
    case 'auth':
      initAuthPage();
      break;
    case 'verify-code':
      initVerifyCodePage();
      break;
    case 'order-received':
      initOrderReceivedPage();
      break;
    default:
      initHomePage();
      break;
  }
};

document.addEventListener('DOMContentLoaded', init);
